import { useMemo, type ReactNode } from "react";
import {
  getCurrentEnvironment,
  isEnvironment,
  isNotEnvironment,
  isDevelopmentEnvironment,
  isProductionEnvironment,
  getLmsDomain,
  isLocalEnvironment,
} from "../utils/environment";
import type { EnvironmentAlias } from "../utils/environment";

/**
 * AI Generated hook to get environment information and helpers
 *
 * @example
 * const { environment, isEnv, isNotEnv, isDev, isProd, lmsDomain } = useEnvironment();
 *
 * // Check current environment
 * if (environment === 'development') { ... }
 *
 * // Check against multiple environments
 * if (isEnv('development', 'alpha')) { ... }
 *
 * // Use aliases
 * if (isEnv('qar', 'qar2')) { ... }
 */
export function useEnvironment() {
  return useMemo(
    () => ({
      /** Current environment name */
      environment: getCurrentEnvironment(),
      /** LMS domain (e.g., "cpattest") */
      lmsDomain: getLmsDomain(),
      /** Check if current env matches any provided */
      isEnv: (...envs: EnvironmentAlias[]) => isEnvironment(...envs),
      /** Check if current env does NOT match any provided */
      isNotEnv: (...envs: EnvironmentAlias[]) => isNotEnvironment(...envs),
      /** True if not production */
      isDev: isDevelopmentEnvironment(),
      /** True if production */
      isProd: isProductionEnvironment(),
    }),
    [],
  );
}

// ============================================================================
// Conditional Rendering Components
// ============================================================================

interface EnvironmentGateProps {
  children: ReactNode;
  /** Fallback content when condition is not met */
  fallback?: ReactNode;
}

interface ShowInEnvironmentProps extends EnvironmentGateProps {
  /** Environment(s) where children should be shown (supports aliases 'qar', 'qar2') */
  env: EnvironmentAlias | EnvironmentAlias[];
}

interface HideInEnvironmentProps extends EnvironmentGateProps {
  /** Environment(s) where children should be hidden (supports aliases 'qar', 'qar2') */
  env: EnvironmentAlias | EnvironmentAlias[];
}

/**
 * Show children only in specified environment(s)
 *
 * @example
 * <ShowInEnvironment env="development">
 *   <DevTools />
 * </ShowInEnvironment>
 *
 * @example
 * <ShowInEnvironment env={['development', 'alpha']}>
 *   <BetaFeature />
 * </ShowInEnvironment>
 *
 * @example
 * <ShowInEnvironment env={['qar', 'qar2']} fallback={<p>Not available</p>}>
 *   <QaTools />
 * </ShowInEnvironment>
 */
export function ShowInEnvironment({ env, children, fallback = null }: ShowInEnvironmentProps) {
  const envArray = Array.isArray(env) ? env : [env];
  return isEnvironment(...envArray) ? <>{children}</> : <>{fallback}</>;
}

/**
 * Hide children in specified environment(s)
 *
 * @example
 * <HideInEnvironment env="production">
 *   <DebugPanel />
 * </HideInEnvironment>
 *
 * @example
 * <HideInEnvironment env={['production', 'beta']}>
 *   <ExperimentalFeature />
 * </HideInEnvironment>
 */
export function HideInEnvironment({ env, children, fallback = null }: HideInEnvironmentProps) {
  const envArray = Array.isArray(env) ? env : [env];
  return isNotEnvironment(...envArray) ? <>{children}</> : <>{fallback}</>;
}

/**
 * Show children only in local developer machines
 *
 * @example
 * <LocalOnly>
 *   <DebugInfo />
 * </LocalOnly>
 */
export function LocalOnly({ children, fallback = null }: EnvironmentGateProps) {
  return isLocalEnvironment() ? <>{children}</> : <>{fallback}</>;
}

/**
 * Show children only in non-production environments
 *
 * @example
 * <DevOnly>
 *   <DebugInfo />
 * </DevOnly>
 */
export function DevOnly({ children, fallback = null }: EnvironmentGateProps) {
  return isDevelopmentEnvironment() ? <>{children}</> : <>{fallback}</>;
}

/**
 * Show children only in production
 *
 * @example
 * <ProdOnly>
 *   <AnalyticsTracker />
 * </ProdOnly>
 */
export function ProdOnly({ children, fallback = null }: EnvironmentGateProps) {
  return isProductionEnvironment() ? <>{children}</> : <>{fallback}</>;
}

// Re-export types and functions for convenience
export type { Environment, EnvironmentAlias } from "../utils/environment";
export {
  getCurrentEnvironment,
  isEnvironment,
  isNotEnvironment,
  isDevelopmentEnvironment,
  isProductionEnvironment,
  getLmsDomain,
} from "../utils/environment";
